import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import logo from './logo.png';
import { getProducts, getMe } from './services/api';
import ShopApp from './pages/ShopApp.jsx';
import AdminLayout from './layouts/AdminLayout.jsx';
import Dashboard from './pages/Admin/Dashboard.jsx';
import Products from './pages/Admin/Products.jsx';
import Categories from './pages/Admin/Categories.jsx';
import Orders from './pages/Admin/Orders.jsx';
import UnprocessedPayments from './pages/Admin/UnprocessedPayments.jsx';
import Transactions from './pages/Admin/Transactions.jsx';
import Users from './pages/Admin/Users.jsx';
import Courier from './pages/Admin/Courier.jsx';
import AdminSupport from './pages/Admin/Support.jsx';
import Settings from './pages/Admin/Settings.jsx';
import Maintenance from './pages/Maintenance.jsx';
import Support from './pages/Support.jsx';
import UserOrders from './pages/Orders.jsx';
import Profile from './pages/Profile.jsx';
import Checkout from './pages/Checkout.jsx';
import Payment from './pages/Payment.jsx';
import PaymentCallback from './pages/PaymentCallback.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import ProfileCompletionGuard from './components/ProfileCompletionGuard.jsx';
import { isProfileComplete } from './utils/profileCompletion';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import './App.css';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for search
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 12,
    total: 0,
    hasMore: false,
    loadingMore: false
  });
  
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        return Array.isArray(parsedCart) ? parsedCart : [];
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return [];
  });
  
  const [wishlist, setWishlist] = useState([]);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [flyingItem, setFlyingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // 'best-selling', '5-star', or null
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true); // Track if we're still checking auth
  const cartButtonRef = React.useRef(null);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Check for existing auth on mount and fetch latest user data
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user');
    
    // First, load user from localStorage for immediate display
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    
    // Then fetch latest user data from server to ensure profile picture is up-to-date
    const fetchLatestUserData = async () => {
      if (token) {
        try {
          const latestUser = await getMe(token);
          setUser(latestUser);
          localStorage.setItem('user', JSON.stringify(latestUser));
        } catch (error) {
          console.error('Error fetching latest user data:', error);
          // If token is invalid, clear auth
          if (error.message.includes('401') || error.message.includes('Unauthenticated')) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      }
      
      // Mark auth check as complete
      setAuthChecking(false);
    };
    
    fetchLatestUserData();
  }, []);

  useEffect(() => {
    const perPage = 12;
    // Fetch products from API (first page only for initial/background; background = no loading spinner)
    const fetchProducts = async (background = false) => {
      try {
        if (!background) {
          setLoading(true);
          setError(null);
        }
        const response = await getProducts(1, perPage);

        if (response && response.data) {
          const productsData = response.data;
          if (background) {
            // Merge fresh first page with existing "load more" data so first 12 stay updated
            setAllProducts(prev => [...productsData, ...prev.slice(perPage)]);
            setPagination(prev => ({
              ...prev,
              currentPage: response.current_page || 1,
              perPage: response.per_page || perPage,
              total: response.total ?? prev.total,
              hasMore: response.has_more !== false && ((response.current_page || 1) * (response.per_page || perPage)) < (response.total || 0),
              loadingMore: false
            }));
          } else {
            setAllProducts(productsData);
            setProducts(productsData);
            setPagination({
              currentPage: response.current_page || 1,
              perPage: response.per_page || perPage,
              total: response.total || productsData.length,
              hasMore: response.has_more !== false && (response.current_page * response.per_page) < response.total,
              loadingMore: false
            });
          }
        } else if (Array.isArray(response)) {
          if (background) {
            setAllProducts(prev => [...response, ...prev.slice(perPage)]);
          } else {
            setAllProducts(response);
            setProducts(response);
            setPagination({
              currentPage: 1,
              perPage: response.length,
              total: response.length,
              hasMore: false,
              loadingMore: false
            });
          }
        } else if (!background) {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        if (!background) {
          console.error('Error fetching products:', err);
          setError(err.message || 'Failed to load products. Please try again later.');
        }
      } finally {
        if (!background) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    // Auto-refresh product list so prices, badges, stock stay in sync with the API
    const REFRESH_INTERVAL_MS = 30000; // 30 seconds
    const refreshId = setInterval(() => fetchProducts(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshId);
  }, []);

  // Load more products function
  const loadMoreProducts = async () => {
    if (pagination.loadingMore || !pagination.hasMore) return;
    
    try {
      setPagination(prev => ({ ...prev, loadingMore: true }));
      const nextPage = pagination.currentPage + 1;
      const response = await getProducts(nextPage, pagination.perPage);
      
      if (response && response.data) {
        const newProducts = response.data;
        setAllProducts(prev => [...prev, ...newProducts]);
        setProducts(prev => [...prev, ...newProducts]);
        setPagination({
          currentPage: response.current_page || nextPage,
          perPage: response.per_page || pagination.perPage,
          total: response.total || pagination.total,
          hasMore: response.has_more !== false && (response.current_page * response.per_page) < response.total,
          loadingMore: false
        });
      } else if (Array.isArray(response)) {
        // Fallback for old API format
        setAllProducts(prev => [...prev, ...response]);
        setProducts(prev => [...prev, ...response]);
        setPagination(prev => ({ ...prev, hasMore: false, loadingMore: false }));
      }
    } catch (err) {
      console.error('Error loading more products:', err);
      setPagination(prev => ({ ...prev, loadingMore: false }));
    }
  };

  // Filter and sort products based on search query and active filter
  useEffect(() => {
    // If there's no search or filter, show products as-is (paginated)
    if (!searchQuery.trim() && !activeFilter) {
      setProducts(allProducts);
      return;
    }
    
    // If there's a search query or filter, filter the loaded products
    let filtered = [...allProducts];

    // Apply search filter first
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const name = (product.name || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const categoryName = product.category?.name ? product.category.name.toLowerCase() : '';
        
        return name.includes(query) || 
               description.includes(query) || 
               categoryName.includes(query);
      });
      // Sort search results by newest first
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    }

    // Apply active filter
    if (activeFilter === 'best-selling') {
      // Sort by sold count (highest first), then by stock if available
      filtered = filtered.sort((a, b) => {
        const soldA = a.sold || a.stock || 0;
        const soldB = b.sold || b.stock || 0;
        return soldB - soldA;
      });
    } else if (activeFilter === '5-star') {
      // Filter products with rating >= 4.8 (essentially 5-star rated)
      filtered = filtered.filter(product => {
        const rating = parseFloat(product.rating) || 0;
        return rating >= 4.8;
      });
      // Sort by rating (highest first), then by reviews
      filtered = filtered.sort((a, b) => {
        const ratingA = parseFloat(a.rating) || 0;
        const ratingB = parseFloat(b.rating) || 0;
        if (ratingB !== ratingA) {
          return ratingB - ratingA;
        }
        return (b.reviews || 0) - (a.reviews || 0);
      });
    } else if (activeFilter && activeFilter.startsWith('category-')) {
      // Filter by category ID
      const categoryId = activeFilter.replace('category-', '');
      filtered = filtered.filter(product => {
        // Handle both API structure (category.id) and direct category_id
        const productCategoryId = product.category?.id || product.category_id;
        return String(productCategoryId) === String(categoryId);
      });
      // Sort by newest first for category filter
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    } else {
      // Default: Sort by newest first when no filter is active
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    }

    setProducts(filtered);
  }, [searchQuery, activeFilter, allProducts]);

  const handleAddToCart = (product, buttonPosition) => {
    const productColors = product.colors && Array.isArray(product.colors) ? product.colors : [];
    const productSizes = product.sizes && Array.isArray(product.sizes) ? product.sizes : [];
    const hasVariants = productColors.length > 0 || productSizes.length > 0;
    
    // If product has variants but no selection (fallback case - should be handled by ProductCard)
    // This is a safety check for edge cases where handleAddToCart is called directly
    if (hasVariants && !product.selectedColor && !product.selectedSize) {
      // Check if there's any available stock across all variants
      let hasAnyStock = false;
      let firstAvailableVariant = null;
      
      // Check variant_stock (color-size combinations)
      if (productColors.length > 0 && productSizes.length > 0 && product.variant_stock && typeof product.variant_stock === 'object') {
        for (const color of productColors) {
          for (const size of productSizes) {
            const variantKey = `${color}-${size}`;
            const variantStock = parseInt(product.variant_stock[variantKey]) || 0;
            const variantSold = parseInt(product.variant_sold?.[variantKey]) || 0;
            if (variantStock - variantSold > 0) {
              hasAnyStock = true;
              firstAvailableVariant = { color, size };
              break;
            }
          }
          if (hasAnyStock) break;
        }
      }
      
      // Check color_stock if no variant stock found
      if (!hasAnyStock && productColors.length > 0 && product.color_stock && typeof product.color_stock === 'object') {
        for (const color of productColors) {
          const colorStock = parseInt(product.color_stock[color]) || 0;
          const colorSold = parseInt(product.color_sold?.[color]) || 0;
          if (colorStock - colorSold > 0) {
            hasAnyStock = true;
            firstAvailableVariant = { color, size: null };
            break;
          }
        }
      }
      
      // If there's stock but no variant selected, use the first available variant
      if (hasAnyStock && firstAvailableVariant) {
        const productWithVariant = {
          ...product,
          selectedColor: firstAvailableVariant.color,
          selectedSize: firstAvailableVariant.size,
          quantity: product.quantity || 1
        };
        // Recursively call with the variant selected
        return handleAddToCart(productWithVariant, buttonPosition);
      }
      
      // If no stock available at all, show out of stock message
      setToast({
        isVisible: true,
        message: 'This product is currently out of stock.',
        type: 'error'
      });
      return;
    }
    
    // Calculate available stock for this product variant
    const normalizedColor = product.selectedColor || null;
    const normalizedSize = product.selectedSize || null;
    
    // Get current cart quantity for this variant
    const existingCartItem = cart.find(item => {
      const itemColor = item.selectedColor || null;
      const itemSize = item.selectedSize || null;
      return item.id === product.id && 
             itemColor === normalizedColor && 
             itemSize === normalizedSize;
    });
    
    const currentCartQuantity = existingCartItem ? existingCartItem.quantity : 0;
    const requestedQuantity = product.quantity || 1;
    const totalRequestedQuantity = currentCartQuantity + requestedQuantity;
    
    // Calculate available stock for this variant
    let availableStock = null;
    
    // Priority: variant_stock > color_stock > general stock
    if (normalizedColor && normalizedSize && productColors.length > 0 && productSizes.length > 0) {
      if (product.variant_stock && typeof product.variant_stock === 'object') {
        const variantKey = `${normalizedColor}-${normalizedSize}`;
        const variantStock = parseInt(product.variant_stock[variantKey]) || 0;
        const variantSold = parseInt(product.variant_sold?.[variantKey]) || 0;
        availableStock = Math.max(0, variantStock - variantSold);
      }
    } else if (normalizedColor && productColors.length > 0 && (!productSizes.length || !normalizedSize)) {
      if (product.color_stock && typeof product.color_stock === 'object') {
        const colorStock = parseInt(product.color_stock[normalizedColor]) || 0;
        const colorSold = parseInt(product.color_sold?.[normalizedColor]) || 0;
        availableStock = Math.max(0, colorStock - colorSold);
      }
    } else {
      const stock = parseInt(product.stock) || 0;
      const sold = parseInt(product.sold) || 0;
      availableStock = Math.max(0, stock - sold);
    }
    
    // Use remainingStock from product if provided (from QuickViewModal)
    if (product.remainingStock !== null && product.remainingStock !== undefined) {
      availableStock = product.remainingStock;
    }
    
    // Validate stock availability
    if (availableStock !== null && availableStock <= 0) {
      setToast({
        isVisible: true,
        message: 'This item is currently out of stock.',
        type: 'error'
      });
      return;
    }
    
    if (availableStock !== null && totalRequestedQuantity > availableStock) {
      const availableToAdd = Math.max(0, availableStock - currentCartQuantity);
      if (availableToAdd <= 0) {
        setToast({
          isVisible: true,
          message: `Only ${availableStock} available in stock. You already have ${currentCartQuantity} in your cart.`,
          type: 'error'
        });
      } else {
        setToast({
          isVisible: true,
          message: `Only ${availableToAdd} more available. You can add up to ${availableStock} total (${currentCartQuantity} already in cart).`,
          type: 'error'
        });
      }
      return;
    }
    
    // Get cart button position
    if (cartButtonRef.current) {
      const cartRect = cartButtonRef.current.getBoundingClientRect();
      const cartCenterX = cartRect.left + cartRect.width / 2;
      const cartCenterY = cartRect.top + cartRect.height / 2;

      // Start flying animation
      if (buttonPosition) {
        setFlyingItem({
          start: buttonPosition,
          end: { x: cartCenterX, y: cartCenterY },
        });
      }
    }

    // Update cart after a small delay to sync with animation
    setTimeout(() => {
      setCart(prevCart => {
        // Check if product with same ID, color, and size already exists in cart
        const existingItem = prevCart.find(item => {
          const itemColor = item.selectedColor || null;
          const itemSize = item.selectedSize || null;
          return item.id === product.id && 
                 itemColor === normalizedColor && 
                 itemSize === normalizedSize;
        });
        
        if (existingItem) {
          // Update quantity (use quantity from product if provided, otherwise increment)
          const newQuantity = product.quantity ? existingItem.quantity + product.quantity : existingItem.quantity + 1;
          
          // Final safety check: ensure we don't exceed available stock
          if (availableStock !== null && newQuantity > availableStock) {
            const maxQuantity = availableStock;
            return prevCart.map(item => {
              const itemColor = item.selectedColor || null;
              const itemSize = item.selectedSize || null;
              return item.id === product.id && 
                     itemColor === normalizedColor && 
                     itemSize === normalizedSize
                ? { ...item, quantity: maxQuantity }
                : item;
            });
          }
          
          return prevCart.map(item => {
            const itemColor = item.selectedColor || null;
            const itemSize = item.selectedSize || null;
            return item.id === product.id && 
                   itemColor === normalizedColor && 
                   itemSize === normalizedSize
              ? { ...item, quantity: newQuantity }
              : item;
          });
        } else {
          // Add new item with selected color, size, and quantity
          return [...prevCart, { 
            ...product, 
            quantity: product.quantity || 1,
            selectedColor: normalizedColor,
            selectedSize: normalizedSize
          }];
        }
      });
    }, 100);
  };

  const handleFlyingComplete = () => {
    setFlyingItem(null);
  };

  // Generate unique cart item ID based on product ID, color, and size
  const getCartItemId = (item) => {
    const color = item.selectedColor || '';
    const size = item.selectedSize || '';
    return `${item.id}_${color}_${size}`;
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }

    setCart(prevCart => {
      const item = prevCart.find(cartItem => getCartItemId(cartItem) === itemId);
      if (!item) return prevCart;

      // Calculate available stock for this product variant
      const normalizedColor = item.selectedColor || null;
      const normalizedSize = item.selectedSize || null;
      
      let availableStock = null;
      const productColors = item.colors && Array.isArray(item.colors) ? item.colors : [];
      const productSizes = item.sizes && Array.isArray(item.sizes) ? item.sizes : [];
      
      // Priority: variant_stock > color_stock > general stock
      if (normalizedColor && normalizedSize && productColors.length > 0 && productSizes.length > 0) {
        if (item.variant_stock && typeof item.variant_stock === 'object') {
          const variantKey = `${normalizedColor}-${normalizedSize}`;
          const variantStock = parseInt(item.variant_stock[variantKey]) || 0;
          const variantSold = parseInt(item.variant_sold?.[variantKey]) || 0;
          availableStock = Math.max(0, variantStock - variantSold);
        }
      } else if (normalizedColor && productColors.length > 0 && (!productSizes.length || !normalizedSize)) {
        if (item.color_stock && typeof item.color_stock === 'object') {
          const colorStock = parseInt(item.color_stock[normalizedColor]) || 0;
          const colorSold = parseInt(item.color_sold?.[normalizedColor]) || 0;
          availableStock = Math.max(0, colorStock - colorSold);
        }
      } else {
        const stock = parseInt(item.stock) || 0;
        const sold = parseInt(item.sold) || 0;
        availableStock = Math.max(0, stock - sold);
      }
      
      // Validate stock availability
      if (availableStock !== null && newQuantity > availableStock) {
        setToast({
          isVisible: true,
          message: `Only ${availableStock} available in stock.`,
          type: 'error'
        });
        // Set quantity to max available
        newQuantity = availableStock;
      }

      return prevCart.map(cartItem => {
        const cartItemId = getCartItemId(cartItem);
        return cartItemId === itemId
          ? { ...cartItem, quantity: newQuantity }
          : cartItem;
      });
    });
  };

  const handleRemoveItem = (itemId) => {
    const item = cart.find(item => getCartItemId(item) === itemId);
    setCart(prevCart => prevCart.filter(item => getCartItemId(item) !== itemId));
    
    // Show toast notification
    if (item) {
      setToast({
        isVisible: true,
        message: `${item.name} removed from cart`,
        type: 'success'
      });
      
      setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 3000);
    }
  };

  const handleToggleWishlist = (product, isAdded) => {
    setWishlist(prevWishlist => {
      if (isAdded) {
        // Add to wishlist
        return [...prevWishlist, product.id];
      } else {
        // Remove from wishlist
        return prevWishlist.filter(id => id !== product.id);
      }
    });
    
    // Show toast notification
    setToast({
      isVisible: true,
      message: isAdded 
        ? 'Added to wishlist' 
        : 'Removed from wishlist',
      type: 'success'
    });
    
    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 3000);
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  };

  // Handle login success with role-based redirect
  const handleLoginSuccess = (user, token, options = {}) => {
    setUser(user);
    
    const { redirectAfterLogin, redirectAfterRegister } = options;
    
    // Check if user is admin (super_admin, administrator, or manager)
    const userRole = user?.role?.name;
    const isAdmin = userRole === 'super_admin' || userRole === 'administrator' || userRole === 'manager';
    const isCourier = userRole === 'courier';
    
    // Check if profile is complete (skip for admin/courier roles)
    const profileComplete = isProfileComplete(user);
    const needsProfileCompletion = !profileComplete && !isAdmin && !isCourier;
    
    // Handle redirects based on context
    if (redirectAfterRegister) {
      // After registration, take user to the shop (homepage)
      navigate('/');
      setToast({
        isVisible: true,
        message: `Welcome, ${user.name || user.email}!`,
        type: 'success'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return;
    }
    
    // If profile is incomplete, redirect to profile page (unless admin/courier)
    if (needsProfileCompletion) {
      navigate('/profile');
      setToast({
        isVisible: true,
        message: `Welcome back, ${user.name || user.email}! Please complete your profile to continue.`,
        type: 'success'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return;
    }
    
    if (redirectAfterLogin) {
      // Profile is complete, proceed to checkout
      navigate('/checkout');
      setToast({
        isVisible: true,
        message: `Welcome back, ${user.name || user.email}!`,
        type: 'success'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return;
    }
    
    // Default behavior: admin/courier redirects
    if (isCourier) {
      // Redirect courier to courier dashboard
      navigate('/admin/courier');
    } else if (isAdmin) {
      // Redirect admin to admin dashboard
      navigate('/admin');
    } else {
      // For regular users logging in from user menu, redirect to homepage
      navigate('/');
    }
    
    setToast({
      isVisible: true,
      message: `Welcome back, ${user.name || user.email}!`,
      type: 'success'
    });
    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 3000);
  };

  // Handle logout
  const handleLogout = useCallback(async (isSessionTimeout = false) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Call API logout to invalidate token on server
        const { logout } = await import('./services/api');
        await logout(token);
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear local storage and state
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      
      // Redirect to home page
      navigate('/');
      
      // Show toast notification with appropriate message
      setToast({
        isVisible: true,
        message: isSessionTimeout 
          ? 'Your session has expired due to inactivity. Please log in again.' 
          : 'You have been logged out successfully.',
        type: isSessionTimeout ? 'error' : 'success'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, isVisible: false }));
      }, 5000); // Show timeout message longer (5 seconds)
    }
  }, [navigate]);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    handleLogout(true);
  }, [handleLogout]);

  // Session timeout hook - logs out user after 30 minutes of inactivity
  useSessionTimeout(handleSessionTimeout, 30, !!user);

  // Handle user update (for profile changes)
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Check site status from settings
  const getSiteStatus = () => {
    try {
      const savedSettings = localStorage.getItem('admin_settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        return settings.site_active !== false; // Default to true if not set
      }
    } catch (error) {
      console.error('Failed to load site status:', error);
    }
    return true; // Default to active if settings not found
  };

  const [siteActive, setSiteActive] = useState(getSiteStatus());

  // Listen for site status changes
  useEffect(() => {
    const handleSiteStatusChange = (event) => {
      setSiteActive(event.detail.site_active);
    };

    window.addEventListener('siteStatusChanged', handleSiteStatusChange);
    
    // Also check on location change
    const currentStatus = getSiteStatus();
    setSiteActive(currentStatus);

    return () => {
      window.removeEventListener('siteStatusChanged', handleSiteStatusChange);
    };
  }, [location]);

  // Check if user is admin (can access admin panel even when site is inactive)
  const isAdmin = user?.role?.name === 'super_admin' || 
                  user?.role?.name === 'administrator' || 
                  user?.role?.name === 'manager' || 
                  user?.role?.name === 'courier';

  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show maintenance page if site is inactive and user is not accessing admin routes
  if (!siteActive && !isAdminRoute && !isAdmin) {
    return <Maintenance />;
  }

  return (
    <Routes>
      {/* Shop Routes */}
      <Route
        path="/"
        element={
          <ProfileCompletionGuard user={user} authChecking={authChecking}>
            <ShopApp
            logo={logo}
            products={products}
            loading={loading}
            error={error}
            cart={cart}
            wishlist={wishlist}
            toast={toast}
            isCartOpen={isCartOpen}
            setIsCartOpen={setIsCartOpen}
            flyingItem={flyingItem}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            pagination={pagination}
            loadMoreProducts={loadMoreProducts}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            handleAddToCart={handleAddToCart}
            handleToggleWishlist={handleToggleWishlist}
            handleQuickView={handleQuickView}
            handleUpdateQuantity={handleUpdateQuantity}
            handleRemoveItem={handleRemoveItem}
            handleCloseToast={handleCloseToast}
            handleFlyingComplete={handleFlyingComplete}
            quickViewProduct={quickViewProduct}
            isQuickViewOpen={isQuickViewOpen}
            setIsQuickViewOpen={setIsQuickViewOpen}
            setQuickViewProduct={setQuickViewProduct}
            cartButtonRef={cartButtonRef}
            user={user}
            onLoginSuccess={handleLoginSuccess}
            onLogout={handleLogout}
          />
          </ProfileCompletionGuard>
        }
      />

      {/* Checkout Route */}
      <Route
        path="/checkout"
        element={
          <ProfileCompletionGuard user={user} authChecking={authChecking}>
            <Checkout
            user={user}
            cart={cart}
            onCartUpdate={setCart}
            onLogout={handleLogout}
            onLoginSuccess={handleLoginSuccess}
          />
          </ProfileCompletionGuard>
        }
      />

      {/* Payment Route */}
      <Route
        path="/payment"
        element={
          <ProtectedRoute user={user} authChecking={authChecking}>
            <Payment
              user={user}
              cart={cart}
              onCartUpdate={setCart}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      {/* Payment Callback Route (Public - handles Paystack redirect) */}
      <Route
        path="/payment/callback"
        element={<PaymentCallback cart={cart} onCartUpdate={setCart} />}
      />

      {/* Orders Route */}
      <Route
        path="/orders"
        element={
          <ProtectedRoute user={user} authChecking={authChecking}>
            <UserOrders
              user={user}
              onUserUpdate={handleUserUpdate}
              onLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      {/* Profile Route */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute user={user} authChecking={authChecking}>
            <Profile
              user={user}
              onUserUpdate={handleUserUpdate}
              onLogout={handleLogout}
              cart={cart}
            />
          </ProtectedRoute>
        }
      />

      {/* Support Route */}
      <Route
        path="/support"
        element={
          <ProtectedRoute user={user} authChecking={authChecking}>
            <Support user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute user={user} requiredRole="admin" authChecking={authChecking}>
              <AdminLayout user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/categories" element={<Categories />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route 
                    path="/unprocessed-payments" 
                    element={
                      <ProtectedRoute user={user} requiredRole="super_admin" authChecking={authChecking}>
                        <UnprocessedPayments />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/transactions" 
                    element={
                      <ProtectedRoute user={user} requiredRole="super_admin" authChecking={authChecking}>
                        <Transactions />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/users" element={<Users />} />
                  <Route path="/courier" element={<Courier />} />
                  <Route path="/support" element={<AdminSupport />} />
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          }
        />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
